/**
 * Client-side input validation utilities.
 *
 * These mirror the backend Pydantic validators to provide immediate UI
 * feedback without waiting for a server round-trip.
 */

/** Validate an email address format. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Validate an IPv4 address. */
export function isValidIPv4(ip: string): boolean {
  return /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) &&
    ip.split('.').every((octet) => {
      const n = parseInt(octet, 10);
      return n >= 0 && n <= 255;
    });
}

/** Validate an IPv6 address (simplified check). */
export function isValidIPv6(ip: string): boolean {
  return /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/.test(ip);
}

/** Validate a CIDR range (e.g., 192.168.1.0/24). */
export function isValidCIDR(cidr: string): boolean {
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;
  const prefix = parseInt(parts[1]!, 10);
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return false;
  return isValidIPv4(parts[0]!);
}

/** Validate a domain name. */
export function isValidDomain(domain: string): boolean {
  return /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(domain);
}

/** Validate a URL. */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/** Validate a port number (1–65535). */
export function isValidPort(port: number): boolean {
  return Number.isInteger(port) && port >= 1 && port <= 65535;
}

/** Validate a CVE ID format (e.g., CVE-2024-12345). */
export function isValidCveId(cve: string): boolean {
  return /^CVE-\d{4}-\d{4,}$/.test(cve);
}

/** Validate a cron expression (basic 5-field check). */
export function isValidCron(cron: string): boolean {
  const parts = cron.trim().split(/\s+/);
  return parts.length === 5;
}

/** Validate a tenant slug (lowercase alphanumeric + hyphens). */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug) && slug.length >= 2 && slug.length <= 63;
}
